import { Autocomplete, FormControl, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { getOptionLabelDependingOnLabelKeys } from '../../utils/componentUtils';
import ReactHookFormErrorLabel from './ReactHookFormErrorLabel';
import { FC, useMemo } from 'react';

interface ReactHookFormAutoCompleteProps {
    name: string;
    label: string;
    control: any;
    options: any[];
    defaultValue?: any;
    labelKeys: string[];
    rules?: any;
    formErrors: any;
    onChange?: (event: any, newValue: any) => void;
    sortByName?: boolean;
    getOptionKey?: (option: any) => string | number;
    getOptionLabel?: (option: any) => string;
    multiple?: boolean;
}

const ReactHookFormAutoComplete: FC<ReactHookFormAutoCompleteProps> = ({
    name,
    label,
    control,
    options,
    defaultValue,
    labelKeys,
    rules,
    formErrors,
    onChange,
    sortByName = true,
    getOptionKey,
    getOptionLabel,
    multiple = false,
    ...props
}) => {
    const sortedOptions = useMemo(() => {
        return sortByName
            ? [...options].sort((a, b) => {
                  if (a.id === -1 || a.name === 'Alle') return -1;
                  if (b.id === -1 || b.name === 'Alle') return 1;
                  const aLabel = getOptionLabelDependingOnLabelKeys(a, labelKeys);
                  const bLabel = getOptionLabelDependingOnLabelKeys(b, labelKeys);
                  return aLabel.localeCompare(bLabel);
              })
            : options || [];
    }, [labelKeys, options, sortByName]);

    const getOptionKeyFunc = useMemo(() => {
        return getOptionKey || ((option: any) => option.id);
    }, [getOptionKey]);

    const getOptionLabelFunc = useMemo(() => {
        return getOptionLabel || ((option: any) => option && getOptionLabelDependingOnLabelKeys(option, labelKeys));
    }, [getOptionLabel, labelKeys]);

    return (
        <FormControl {...props} fullWidth={true}>
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                rules={rules}
                render={({ field, fieldState }) => (
                    <>
                        <Autocomplete
                            {...field}
                            onChange={(event, newValue) => {
                                onChange && onChange(event, newValue);
                                field.onChange(newValue);
                            }}
                            options={sortedOptions}
                            getOptionKey={getOptionKeyFunc}
                            multiple={multiple}
                            getOptionLabel={getOptionLabelFunc}
                            renderInput={(params) => <TextField {...params} variant="outlined" label={label} />}
                            {...props}
                            value={field.value || null}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="Keine Optionen"
                        />
                        {fieldState?.error && (
                            <ReactHookFormErrorLabel
                                fieldError={fieldState.error}
                                fieldName={name}
                                formErrors={formErrors}
                            ></ReactHookFormErrorLabel>
                        )}
                    </>
                )}
            />
        </FormControl>
    );
};

export default ReactHookFormAutoComplete;
